/**
 * PUBLISHER domain state — releases, their payments, and their event history.
 *
 * ============================================================================
 * PERSISTED, WHICH IS UNUSUAL IN THIS APP AND DELIBERATE HERE.
 *
 * Songs, credits and the player are all in memory by design (see uiStore's note:
 * the collapse preference is the only other persisted state). A release draft is
 * different: §28 asks that releases, their status and their payments survive, and
 * a distribution record is not a view preference — losing a half-completed
 * release on reload, or worse, losing the record that a fee was paid, is data
 * loss rather than a forgotten setting.
 *
 * It is still CLIENT-SIDE ONLY and therefore NOT AUTHORITATIVE. A real
 * deployment's release records live server-side; this store is what the UI reads
 * until then, and the same caveat that governs entitlementsStore governs this —
 * everything in it can be edited from devtools. Notably, `payment.verifiedAt` is
 * never written by this store at all, precisely because a client cannot verify a
 * payment. See api/publisherApi.submitRelease.
 * ============================================================================
 */
import { create } from 'zustand';
import { mockSeedLibrary } from '../api/mockCatalogue';
import { PUBLISHING_FEE, PUBLISHING_FEE_CURRENCY, publisherApi, type PublisherDataMode } from '../api/publisherApi';
import { IS_DEV_ENV, USE_MOCK_API } from '../config';
import publisherDashboardMock from '../data/publisherDashboardMock.json';
import { newId } from '../lib/ids';
import { materializePublisherMockSnapshot } from '../lib/publisherDashboard';
import { useAuthStore } from './authStore';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';
import { usePublisherCreditsStore } from './publisherCreditsStore';
import type {
  PublisherPayment,
  PublisherRelease,
  PublisherReleaseEvent,
  PublisherReleaseEventType,
  PublisherReleasePlatform,
  PublisherReleaseStatus,
  PublisherReleaseType,
  PublisherStateSnapshot,
} from '../types/domain';

/** Where the release records live. */
const STORAGE_KEY = 'Lyrixa.publisher';
const ANONYMOUS_ACCOUNT_KEY = '__anonymous__';
const RELEASE_DELETE_GRACE_MS = 24 * 60 * 60 * 1000;

/** What is written to storage. Versioned so a future shape change can migrate. */
export interface PublisherWizardProgress {
  /** The numbered wizard step the user last stopped on. */
  step: number;
  /** The steps they have already visited. */
  visited: number[];
  /** Whether they were still assembling the release or already at payment. */
  phase: 'wizard' | 'payment';
  /** Which draft was touched most recently. */
  touchedAt: string;
}

/** Which Publisher screen should reopen when the user re-enters the section. */
export type PublisherLastVisibleSurface =
  | { kind: 'dashboard' }
  | { kind: 'wizard'; releaseId: string | null }
  | { kind: 'detail'; releaseId: string };

export interface PendingDashboardDeletion {
  requestedAt: string;
  purgeAfter: string;
}

interface PersistedBucket {
  releases: PublisherRelease[];
  payments: PublisherPayment[];
  events: PublisherReleaseEvent[];
  platforms: PublisherReleasePlatform[];
  wizardProgress: Record<string, PublisherWizardProgress>;
  lastVisibleSurface: PublisherLastVisibleSurface | null;
  pendingDashboardDeletions: Record<string, PendingDashboardDeletion>;
}

interface PersistedShape {
  version: 6;
  accounts: Record<string, PersistedBucket>;
}

interface PublisherAccount {
  key: string;
  isAdmin: boolean;
}

const SIMULATED_RELEASE_OUTLETS = ['spotify', 'apple-music', 'tidal'] as const;
const SIMULATED_PROVIDER_EVENT_MESSAGES = ['Release Approved', 'Release Distributed'] as const;
const SIMULATED_OUTLET_LABELS: Readonly<Record<string, string>> = {
  spotify: 'Spotify',
  'apple-music': 'Apple Music',
  tidal: 'TIDAL',
  deezer: 'Deezer',
  'youtube-music': 'YouTube Music',
  'amazon-music': 'Amazon Music',
};

function emptyPersistedBucket(): PersistedBucket {
  return {
    releases: [],
    payments: [],
    events: [],
    platforms: [],
    wizardProgress: {},
    lastVisibleSurface: null,
    pendingDashboardDeletions: {},
  };
}

/**
 * Reads persisted releases, tolerating every way storage can fail.
 *
 * The try/catch is the same defence uiStore documents — Safari private mode throws on
 * access — plus one more case that matters for JSON: a corrupted or half-written value
 * would throw on parse, and a Publisher that refuses to render because a stale record
 * could not be read would be worse than one that starts empty.
 *
 * The shape is checked rather than trusted. This value is user-editable, so a
 * `releases` that is not an array has to be discarded rather than handed to `.map`.
 */
function isWizardProgress(value: unknown): value is PublisherWizardProgress {
  if (typeof value !== 'object' || value === null) return false;
  const progress = value as Partial<PublisherWizardProgress>;
  return (
    typeof progress.step === 'number' &&
    Array.isArray(progress.visited) &&
    progress.visited.every((step) => typeof step === 'number') &&
    (progress.phase === 'wizard' || progress.phase === 'payment') &&
    typeof progress.touchedAt === 'string'
  );
}

function isPublisherLastVisibleSurface(value: unknown): value is PublisherLastVisibleSurface {
  if (typeof value !== 'object' || value === null) return false;
  const surface = value as Partial<PublisherLastVisibleSurface> & { releaseId?: unknown };
  if (surface.kind === 'dashboard') return true;
  if (surface.kind === 'wizard') return surface.releaseId === null || typeof surface.releaseId === 'string';
  if (surface.kind === 'detail') return typeof surface.releaseId === 'string';
  return false;
}

function isPendingDashboardDeletion(value: unknown): value is PendingDashboardDeletion {
  if (typeof value !== 'object' || value === null) return false;
  const pending = value as Partial<PendingDashboardDeletion>;
  return typeof pending.requestedAt === 'string' && typeof pending.purgeAfter === 'string';
}

function normalisePersistedBucket(value: unknown): PersistedBucket {
  if (typeof value !== 'object' || value === null) return emptyPersistedBucket();
  const bucket = value as Partial<PersistedBucket> & {
    wizardProgress?: unknown;
    lastVisibleSurface?: unknown;
    pendingDashboardDeletions?: unknown;
  };
  return {
    releases: Array.isArray(bucket.releases) ? bucket.releases : [],
    payments: Array.isArray(bucket.payments) ? bucket.payments : [],
    events: Array.isArray(bucket.events) ? bucket.events : [],
    platforms: Array.isArray(bucket.platforms) ? bucket.platforms : [],
    wizardProgress:
      typeof bucket.wizardProgress === 'object' && bucket.wizardProgress !== null
        ? Object.fromEntries(
            Object.entries(bucket.wizardProgress).filter((entry): entry is [string, PublisherWizardProgress] =>
              isWizardProgress(entry[1])
            )
          )
        : {},
    lastVisibleSurface: isPublisherLastVisibleSurface(bucket.lastVisibleSurface)
      ? bucket.lastVisibleSurface
      : null,
    pendingDashboardDeletions:
      typeof bucket.pendingDashboardDeletions === 'object' && bucket.pendingDashboardDeletions !== null
        ? Object.fromEntries(
            Object.entries(bucket.pendingDashboardDeletions).filter(
              (entry): entry is [string, PendingDashboardDeletion] =>
                isPendingDashboardDeletion(entry[1])
            )
          )
        : {},
  };
}

function currentPublisherAccount(): PublisherAccount {
  const user = useAuthStore.getState().user;
  return {
    key: user?.id ?? ANONYMOUS_ACCOUNT_KEY,
    isAdmin: user?.isAdmin === true,
  };
}

function readPersistedRoot(): PersistedShape {
  const empty: PersistedShape = { version: 6, accounts: {} };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return empty;

    const shape = parsed as Partial<PersistedShape> & {
      accounts?: unknown;
      releases?: unknown;
      payments?: unknown;
      events?: unknown;
      platforms?: unknown;
      wizardProgress?: unknown;
      lastVisibleSurface?: unknown;
    };

    if (typeof shape.accounts === 'object' && shape.accounts !== null) {
      return {
        version: 6,
        accounts: Object.fromEntries(
          Object.entries(shape.accounts)
            .filter(([accountKey]) => typeof accountKey === 'string' && accountKey.length > 0)
            .map(([accountKey, bucket]) => [accountKey, normalisePersistedBucket(bucket)])
        ),
      };
    }

    /*
      MIGRATES THE OLD GLOBAL SHAPE FORWARD.

      Version 3 held one browser-wide Publisher bucket. Version 4 scoped that same data by
      account, version 5 adds the last visible Publisher surface, and version 6 adds the
      24-hour dashboard-delete grace window. When an older value is encountered, it is adopted
      as the current account's bucket on first read so existing local drafts are not discarded.
    */
    return {
      version: 6,
      accounts: {
        [currentPublisherAccount().key]: normalisePersistedBucket(shape),
      },
    };
  } catch {
    return empty;
  }
}

/** Writes the records, ignoring a storage that refuses. */
function writePersistedRoot(shape: PersistedShape): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shape));
  } catch {
    // Quota exceeded, or storage disabled. The Publisher still works for this session.
  }
}

function readPersistedBucket(accountKey: string): PersistedBucket {
  const root = readPersistedRoot();
  const bucket = root.accounts[accountKey];
  if (!bucket) return emptyPersistedBucket();
  const purged = purgeExpiredDashboardDeletionsFromBucket(bucket, Date.now());
  if (purged.changed) {
    writePersistedRoot({
      version: 6,
      accounts: {
        ...root.accounts,
        [accountKey]: purged.bucket,
      },
    });
  }
  return purged.bucket;
}

function writePersistedBucket(accountKey: string, bucket: PersistedBucket): void {
  const root = readPersistedRoot();
  writePersistedRoot({
    version: 6,
    accounts: {
      ...root.accounts,
      [accountKey]: bucket,
    },
  });
}

function bucketHasData(bucket: PersistedBucket): boolean {
  return (
    bucket.releases.length > 0 ||
    bucket.payments.length > 0 ||
    bucket.events.length > 0 ||
    bucket.platforms.length > 0 ||
    Object.keys(bucket.wizardProgress).length > 0 ||
    bucket.lastVisibleSurface !== null ||
    Object.keys(bucket.pendingDashboardDeletions).length > 0
  );
}

function movePersistedBucket(fromKey: string, toKey: string): PersistedBucket {
  const root = readPersistedRoot();
  const source = root.accounts[fromKey] ?? emptyPersistedBucket();
  if (!bucketHasData(source)) return emptyPersistedBucket();
  const { [fromKey]: _removed, ...rest } = root.accounts;
  writePersistedRoot({
    version: 6,
    accounts: {
      ...rest,
      [toKey]: source,
    },
  });
  return source;
}

function hasPersistedPublisherValue(accountKey: string): boolean {
  try {
    const root = readPersistedRoot();
    return Object.prototype.hasOwnProperty.call(root.accounts, accountKey);
  } catch {
    return false;
  }
}

function withoutWizardProgress(
  wizardProgress: Record<string, PublisherWizardProgress>,
  releaseId: string
): Record<string, PublisherWizardProgress> {
  if (!(releaseId in wizardProgress)) return wizardProgress;
  const { [releaseId]: _removed, ...rest } = wizardProgress;
  return rest;
}

function withoutReleaseSurface(
  surface: PublisherLastVisibleSurface | null,
  releaseId: string
): PublisherLastVisibleSurface | null {
  if (!surface) return surface;
  if (surface.kind === 'dashboard') return surface;
  if (surface.kind === 'wizard' && surface.releaseId === releaseId) return { kind: 'dashboard' };
  if (surface.kind === 'detail' && surface.releaseId === releaseId) return { kind: 'dashboard' };
  return surface;
}

function withoutPendingDashboardDeletion(
  pendingDashboardDeletions: Record<string, PendingDashboardDeletion>,
  releaseId: string
): Record<string, PendingDashboardDeletion> {
  if (!(releaseId in pendingDashboardDeletions)) return pendingDashboardDeletions;
  const { [releaseId]: _removed, ...rest } = pendingDashboardDeletions;
  return rest;
}

function pendingDashboardDeletionExpired(
  pendingDashboardDeletion: PendingDashboardDeletion,
  nowMs: number
): boolean {
  const purgeAfterMs = Date.parse(pendingDashboardDeletion.purgeAfter);
  return Number.isNaN(purgeAfterMs) || purgeAfterMs <= nowMs;
}

function purgeReleaseFromBucket(bucket: PersistedBucket, releaseId: string): PersistedBucket {
  return {
    releases: bucket.releases.filter((release) => release.id !== releaseId),
    payments: bucket.payments.filter((payment) => payment.releaseId !== releaseId),
    events: bucket.events.filter((event) => event.releaseId !== releaseId),
    platforms: bucket.platforms.filter((platform) => platform.releaseId !== releaseId),
    wizardProgress: withoutWizardProgress(bucket.wizardProgress, releaseId),
    lastVisibleSurface: withoutReleaseSurface(bucket.lastVisibleSurface, releaseId),
    pendingDashboardDeletions: withoutPendingDashboardDeletion(
      bucket.pendingDashboardDeletions,
      releaseId
    ),
  };
}

function purgeExpiredDashboardDeletionsFromBucket(
  bucket: PersistedBucket,
  nowMs: number
): { bucket: PersistedBucket; changed: boolean } {
  const releaseIds = new Set(bucket.releases.map((release) => release.id));
  let next = bucket;
  let changed = false;

  for (const [releaseId, pending] of Object.entries(bucket.pendingDashboardDeletions)) {
    if (!releaseIds.has(releaseId) || pendingDashboardDeletionExpired(pending, nowMs)) {
      next = releaseIds.has(releaseId)
        ? purgeReleaseFromBucket(next, releaseId)
        : {
            ...next,
            pendingDashboardDeletions: withoutPendingDashboardDeletion(
              next.pendingDashboardDeletions,
              releaseId
            ),
          };
      changed = true;
    }
  }

  return { bucket: next, changed };
}

function nextWizardTouchedAt(wizardProgress: Record<string, PublisherWizardProgress>): string {
  const now = new Date().toISOString();
  const latest = Object.values(wizardProgress).reduce(
    (max, progress) => (progress.touchedAt > max ? progress.touchedAt : max),
    ''
  );
  if (!latest || now > latest) return now;
  return new Date(Date.parse(latest) + 1).toISOString();
}

function outletNameForSimulation(outletKey: string): string {
  const known = SIMULATED_OUTLET_LABELS[outletKey];
  if (known) return known;
  return outletKey
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function simulatedOutletKeysFor(release: PublisherRelease): string[] {
  return release.outletKeys.length > 0 ? release.outletKeys : [...SIMULATED_RELEASE_OUTLETS];
}

function simulatedBarcodeFor(release: PublisherRelease): string {
  return release.barcode ?? `060244579${String(release.seq).padStart(4, '0')}`;
}

function simulatedPlatformRows(
  releaseId: string,
  outletKeys: string[],
  nowMs: number
): PublisherReleasePlatform[] {
  return outletKeys.map((outletKey, index) => ({
    releaseId,
    outletKey,
    outletName: outletNameForSimulation(outletKey),
    status: 'complete',
    updatedAt: new Date(nowMs - (outletKeys.length - index) * 60_000).toISOString(),
  }));
}

function simulatedProviderEvents(releaseId: string, nowMs: number): PublisherReleaseEvent[] {
  return [
    {
      id: newId(),
      releaseId,
      at: new Date(nowMs - 30 * 60 * 1000).toISOString(),
      message: 'Release Approved',
      detail: 'The distributor accepted the release metadata and queued outlet delivery.',
      actionLabel: 'Open release',
      type: 'milestone',
      source: 'provider',
    },
    {
      id: newId(),
      releaseId,
      at: new Date(nowMs - 5 * 60 * 1000).toISOString(),
      message: 'Release Distributed',
      detail: 'Provider-backed delivery updates have been recorded for this release.',
      actionLabel: 'Open release',
      type: 'platform_update',
      source: 'provider',
    },
  ];
}

function simulatedReleasedRecord(release: PublisherRelease, nowMs: number): PublisherRelease {
  const outletKeys = simulatedOutletKeysFor(release);
  return {
    ...release,
    outletKeys,
    releaseDate: release.releaseDate || new Date(nowMs - 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'live',
    externalId: release.externalId ?? 9000 + release.seq,
    providerReviewStatus: 'approved',
    providerDeliveryStatus: 'distributed',
    barcode: simulatedBarcodeFor(release),
    updatedAt: new Date(nowMs).toISOString(),
    failureReason: null,
  };
}

function developerSeededReleasedState(account: PublisherAccount, bucket: PersistedBucket): PersistedBucket {
  if (!USE_MOCK_API) return bucket;
  if (!IS_DEV_ENV) return bucket;
  if (!account.isAdmin) return bucket;
  if (hasPersistedPublisherValue(account.key)) return bucket;
  if (bucketHasData(bucket)) return bucket;

  /*
    DASHBOARD DEMO DATA, ONLY FOR THE ADMIN ACCOUNT AND ONLY WHEN THAT ACCOUNT'S PUBLISHER IS
    OTHERWISE EMPTY.

    The original seed was one hardcoded released record. The dashboard now needs a richer but
    still read-only operational snapshot, so the seed comes from the same JSON fixture the mock
    API returns. The one runtime rewrite it needs is the seeded-song references: the committed
    JSON cannot know the process-local ids that mockSeedLibrary() minted, so those placeholders
    are resolved here before the snapshot is persisted.
  */
  const snapshot = materializePublisherMockSnapshot(
    publisherDashboardMock as PublisherStateSnapshot,
    mockSeedLibrary()
  );
  const seeded: PersistedBucket = {
    releases: snapshot.releases,
    payments: snapshot.payments,
    events: snapshot.events,
    platforms: snapshot.platforms,
    wizardProgress: {},
    lastVisibleSurface: null,
    pendingDashboardDeletions: {},
  };
  writePersistedBucket(account.key, seeded);
  return seeded;
}

type PublisherEventMeta = {
  source?: 'local' | 'provider';
  type?: PublisherReleaseEventType;
  detail?: string;
  actionLabel?: string;
};

type PublisherEventInput = PublisherEventMeta | 'local' | 'provider';

interface PublisherState {
  releases: PublisherRelease[];
  payments: PublisherPayment[];
  events: PublisherReleaseEvent[];
  platforms: PublisherReleasePlatform[];
  wizardProgress: Record<string, PublisherWizardProgress>;
  lastVisibleSurface: PublisherLastVisibleSurface | null;
  pendingDashboardDeletions: Record<string, PendingDashboardDeletion>;
  /** Where the backend hydration has got to. */
  load: LoadState;
  /** Monotonic ordering key, as on songsStore. */
  seq: number;
  /**
   * Release ids with a submission in flight.
   *
   * §27's double-click protection, and it is state rather than a local `useState`
   * because the guard has to hold across the two surfaces that can submit (the wizard's
   * review step and a retry from the detail page). A local flag in one component would
   * not stop the other.
   */
  submitting: string[];

  createRelease: (input: {
    releaseType: PublisherReleaseType;
    title?: string;
    artistName?: string;
    songIds?: string[];
  }) => PublisherRelease;
  updateRelease: (id: string, patch: Partial<PublisherRelease>) => void;
  /** Final purge, used after the grace window expires. */
  deleteRelease: (id: string) => void;
  scheduleReleaseDeletion: (id: string) => void;
  undoReleaseDeletion: (id: string) => void;
  purgeExpiredReleaseDeletions: () => void;
  setStatus: (id: string, status: PublisherReleaseStatus, failureReason?: string) => void;
  /** Records something that happened, with a real timestamp. */
  addEvent: (releaseId: string, message: string, meta?: PublisherEventInput) => void;
  /** Creates the unpaid fee record for a release, or returns the existing one. */
  ensurePayment: (releaseId: string) => PublisherPayment;
  /** Patches a stored payment, including the fields only a server may normally write. */
  updatePayment: (id: string, patch: Partial<PublisherPayment>) => void;
  /** Seeds a developer-only, provider-shaped released state into the local mock store. */
  simulateReleasedRelease: (releaseId: string) => void;
  /** The stored platform rows for one release, as the mock API would read them. */
  platformRowsFor: (releaseId: string) => PublisherReleasePlatform[];
  setWizardProgress: (releaseId: string, progress: Omit<PublisherWizardProgress, 'touchedAt'>) => void;
  clearWizardProgress: (releaseId: string) => void;
  setLastVisibleSurface: (surface: PublisherLastVisibleSurface) => void;
  beginSubmit: (releaseId: string) => boolean;
  endSubmit: (releaseId: string) => void;
  loadPublisher: (mode?: PublisherDataMode) => Promise<void>;
  hydrate: (snapshot: PublisherStateSnapshot, options?: { persist?: boolean }) => void;
  reset: () => void;
}

let activeAccount = currentPublisherAccount();
const persisted = developerSeededReleasedState(activeAccount, readPersistedBucket(activeAccount.key));

function nextSeqFor(bucket: PersistedBucket): number {
  return bucket.releases.reduce((max: number, release: PublisherRelease) => Math.max(max, release.seq), 0);
}

function persistedBucketFromState(state: Pick<
  PublisherState,
  | 'releases'
  | 'payments'
  | 'events'
  | 'platforms'
  | 'wizardProgress'
  | 'lastVisibleSurface'
  | 'pendingDashboardDeletions'
>): PersistedBucket {
  return {
    releases: state.releases,
    payments: state.payments,
    events: state.events,
    platforms: state.platforms,
    wizardProgress: state.wizardProgress,
    lastVisibleSurface: state.lastVisibleSurface,
    pendingDashboardDeletions: state.pendingDashboardDeletions,
  };
}

function bucketFromSnapshot(
  snapshot: PublisherStateSnapshot,
  previous: Pick<PublisherState, 'wizardProgress' | 'lastVisibleSurface' | 'pendingDashboardDeletions'>
): PersistedBucket {
  return {
    releases: snapshot.releases,
    payments: snapshot.payments,
    events: snapshot.events,
    platforms: snapshot.platforms,
    wizardProgress: previous.wizardProgress,
    lastVisibleSurface: previous.lastVisibleSurface,
    pendingDashboardDeletions: previous.pendingDashboardDeletions,
  };
}

export const usePublisherStore = create<PublisherState>((set, get) => ({
  releases: persisted.releases,
  payments: persisted.payments,
  events: persisted.events,
  platforms: persisted.platforms,
  wizardProgress: persisted.wizardProgress,
  lastVisibleSurface: persisted.lastVisibleSurface,
  pendingDashboardDeletions: persisted.pendingDashboardDeletions,
  load: IDLE_LOAD,
  // Highest existing seq, so ordering survives a reload rather than restarting at 0 and
  // interleaving new releases among old ones.
  seq: nextSeqFor(persisted),
  submitting: [],

  createRelease(input) {
    const now = new Date().toISOString();
    const seq = get().seq + 1;
    const release: PublisherRelease = {
      id: newId(),
      seq,
      releaseType: input.releaseType,
      title: input.title ?? '',
      artistName: input.artistName ?? '',
      writerContributor: input.artistName ?? '',
      rightsAttested: false,
      songIds: input.songIds ?? [],
      releaseDate: '',
      genre: '',
      language: '',
      explicit: false,
      trackLyrics: {},
      lyrics: '',
      artworkUrl: null,
      artworkFromSongId: null,
      artworkPrompt: '',
      artworkOrigin: null,
      outletKeys: [],
      status: 'draft',
      // Null, and it stays null until a real provider response supplies one. §29.
      externalId: null,
      providerReviewStatus: null,
      providerDeliveryStatus: null,
      barcode: null,
      createdAt: now,
      updatedAt: now,
      failureReason: null,
    };
    set((s) => {
      const releases = [release, ...s.releases];
      const bucket = {
        ...persistedBucketFromState(s),
        releases,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { releases, seq };
    });
    return release;
  },

  updateRelease(id, patch) {
    set((s) => {
      const releases = s.releases.map((r) =>
        r.id === id ? { ...r, ...patch, updatedAt: new Date().toISOString() } : r
      );
      const bucket = {
        ...persistedBucketFromState(s),
        releases,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { releases };
    });
  },

  deleteRelease(id) {
    set((s) => {
      const bucket = purgeReleaseFromBucket(persistedBucketFromState(s), id);
      writePersistedBucket(activeAccount.key, bucket);
      return {
        releases: bucket.releases,
        payments: bucket.payments,
        events: bucket.events,
        platforms: bucket.platforms,
        wizardProgress: bucket.wizardProgress,
        lastVisibleSurface: bucket.lastVisibleSurface,
        pendingDashboardDeletions: bucket.pendingDashboardDeletions,
      };
    });
  },

  scheduleReleaseDeletion(id) {
    set((s) => {
      if (!s.releases.some((release) => release.id === id)) return s;
      if (s.pendingDashboardDeletions[id]) return s;
      const nowMs = Date.now();
      const pendingDashboardDeletions = {
        ...s.pendingDashboardDeletions,
        [id]: {
          requestedAt: new Date(nowMs).toISOString(),
          purgeAfter: new Date(nowMs + RELEASE_DELETE_GRACE_MS).toISOString(),
        },
      };
      const lastVisibleSurface = withoutReleaseSurface(s.lastVisibleSurface, id);
      const bucket = {
        ...persistedBucketFromState(s),
        lastVisibleSurface,
        pendingDashboardDeletions,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { pendingDashboardDeletions, lastVisibleSurface };
    });
  },

  undoReleaseDeletion(id) {
    set((s) => {
      const pendingDashboardDeletions = withoutPendingDashboardDeletion(s.pendingDashboardDeletions, id);
      if (pendingDashboardDeletions === s.pendingDashboardDeletions) return s;
      const bucket = {
        ...persistedBucketFromState(s),
        pendingDashboardDeletions,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { pendingDashboardDeletions };
    });
  },

  purgeExpiredReleaseDeletions() {
    set((s) => {
      const purged = purgeExpiredDashboardDeletionsFromBucket(persistedBucketFromState(s), Date.now());
      if (!purged.changed) return s;
      writePersistedBucket(activeAccount.key, purged.bucket);
      return {
        releases: purged.bucket.releases,
        payments: purged.bucket.payments,
        events: purged.bucket.events,
        platforms: purged.bucket.platforms,
        wizardProgress: purged.bucket.wizardProgress,
        lastVisibleSurface: purged.bucket.lastVisibleSurface,
        pendingDashboardDeletions: purged.bucket.pendingDashboardDeletions,
      };
    });
  },

  setStatus(id, status, failureReason) {
    get().updateRelease(id, {
      status,
      // Cleared on any status that is not a failure, so a resolved problem does not keep
      // showing its old reason.
      failureReason: status === 'failed' ? (failureReason ?? null) : null,
    });
  },

  addEvent(releaseId, message, meta = 'local') {
    const details: PublisherEventMeta = typeof meta === 'string' ? { source: meta } : meta;
    const event: PublisherReleaseEvent = {
      id: newId(),
      releaseId,
      // The client clock for a local action. A provider event carries the provider's own
      // timestamp and is added by whatever read it — never synthesised here. §26.
      at: new Date().toISOString(),
      message,
      ...(details.detail ? { detail: details.detail } : {}),
      ...(details.actionLabel ? { actionLabel: details.actionLabel } : {}),
      ...(details.type ? { type: details.type } : {}),
      source: details.source ?? 'local',
    };
    set((s) => {
      const events = [...s.events, event];
      const bucket = {
        ...persistedBucketFromState(s),
        events,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { events };
    });
  },

  ensurePayment(releaseId) {
    /*
      IDEMPOTENT BY DESIGN — §27's duplicate-charge protection starts here.

      One payment record per release, created once and reused. The idempotency key is
      generated with it and never regenerated, so a retry of a failed charge presents the
      same key and the provider charges once. Regenerating the key on retry is the bug
      this shape exists to prevent.
    */
    const existing = get().payments.find((p) => p.releaseId === releaseId);
    if (existing) return existing;

    const payment: PublisherPayment = {
      id: newId(),
      releaseId,
      amount: PUBLISHING_FEE,
      currency: PUBLISHING_FEE_CURRENCY,
      status: 'unpaid',
      idempotencyKey: newId(),
      createdAt: new Date().toISOString(),
      // Never set by this store. Only a server can verify a charge. §17.
      verifiedAt: null,
      failureReason: null,
    };
    set((s) => {
      const payments = [...s.payments, payment];
      const bucket = {
        ...persistedBucketFromState(s),
        payments,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { payments };
    });
    return payment;
  },

  updatePayment(id: string, patch: Partial<PublisherPayment>) {
    set((s) => {
      const payments = s.payments.map((p) => (p.id === id ? { ...p, ...patch } : p));
      const bucket = {
        ...persistedBucketFromState(s),
        payments,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { payments };
    });
  },

  simulateReleasedRelease(releaseId) {
    set((s) => {
      const release = s.releases.find((r) => r.id === releaseId);
      if (!release) return s;

      const nowMs = Date.now();
      const simulated = simulatedReleasedRecord(release, nowMs);
      const releases = s.releases.map((r) => (r.id === releaseId ? simulated : r));
      const events = [
        ...s.events.filter(
          (event) =>
            event.releaseId !== releaseId ||
            !SIMULATED_PROVIDER_EVENT_MESSAGES.includes(
              event.message as (typeof SIMULATED_PROVIDER_EVENT_MESSAGES)[number]
            )
        ),
        ...simulatedProviderEvents(releaseId, nowMs),
      ];
      const platforms = [
        ...s.platforms.filter((platform) => platform.releaseId !== releaseId),
        ...simulatedPlatformRows(releaseId, simulated.outletKeys, nowMs),
      ];
      const wizardProgress = withoutWizardProgress(s.wizardProgress, releaseId);
      const lastVisibleSurface = withoutReleaseSurface(s.lastVisibleSurface, releaseId);
      const bucket = {
        ...persistedBucketFromState(s),
        releases,
        events,
        platforms,
        wizardProgress,
        lastVisibleSurface,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { releases, events, platforms, wizardProgress, lastVisibleSurface };
    });
  },

  platformRowsFor(releaseId) {
    return get().platforms.filter((platform) => platform.releaseId === releaseId);
  },

  setWizardProgress(releaseId, progress) {
    set((s) => {
      const current = s.wizardProgress[releaseId];
      if (
        current &&
        current.step === progress.step &&
        current.phase === progress.phase &&
        current.visited.length === progress.visited.length &&
        current.visited.every((value, index) => value === progress.visited[index])
      ) {
        return s;
      }
      const wizardProgress = {
        ...s.wizardProgress,
        [releaseId]: {
          ...progress,
          touchedAt: nextWizardTouchedAt(s.wizardProgress),
        },
      };
      const bucket = {
        ...persistedBucketFromState(s),
        wizardProgress,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { wizardProgress };
    });
  },

  clearWizardProgress(releaseId) {
    set((s) => {
      const wizardProgress = withoutWizardProgress(s.wizardProgress, releaseId);
      if (wizardProgress === s.wizardProgress) return s;
      const bucket = {
        ...persistedBucketFromState(s),
        wizardProgress,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { wizardProgress };
    });
  },

  setLastVisibleSurface(lastVisibleSurface) {
    set((s) => {
      if (JSON.stringify(s.lastVisibleSurface) === JSON.stringify(lastVisibleSurface)) return s;
      const bucket = {
        ...persistedBucketFromState(s),
        lastVisibleSurface,
      };
      writePersistedBucket(activeAccount.key, bucket);
      return { lastVisibleSurface };
    });
  },

  /**
   * Claims the submission slot for a release.
   *
   * Returns false when one is already in flight, which is how a double-click is refused:
   * the caller must not proceed on false. A boolean return rather than a thrown error
   * because "someone clicked twice" is not exceptional.
   */
  beginSubmit(releaseId) {
    if (get().submitting.includes(releaseId)) return false;
    set((s) => ({ submitting: [...s.submitting, releaseId] }));
    return true;
  },

  endSubmit(releaseId) {
    set((s) => ({ submitting: s.submitting.filter((id) => id !== releaseId) }));
  },

  async loadPublisher(mode) {
    if (mode === 'real') {
      const bucket = readPersistedBucket(activeAccount.key);
      set({
        releases: bucket.releases,
        payments: bucket.payments,
        events: bucket.events,
        platforms: bucket.platforms,
        wizardProgress: bucket.wizardProgress,
        lastVisibleSurface: bucket.lastVisibleSurface,
        pendingDashboardDeletions: bucket.pendingDashboardDeletions,
        seq: nextSeqFor(bucket),
      });
    }
    set({ load: LOADING_LOAD });
    try {
      const snapshot = await publisherApi.getState({ mode });
      get().hydrate(snapshot, { persist: mode !== 'mock' });
      if (mode !== 'mock' && snapshot.publisherCredits) {
        usePublisherCreditsStore.getState().hydrate(snapshot.publisherCredits);
      }
      set({ load: READY_LOAD });
    } catch (err) {
      set({ load: failedLoad(err) });
    }
  },

  hydrate(snapshot, options = {}) {
    set((s) => {
      const bucket = bucketFromSnapshot(snapshot, s);
      const purged = purgeExpiredDashboardDeletionsFromBucket(bucket, Date.now());
      if (options.persist !== false) {
        writePersistedBucket(activeAccount.key, purged.bucket);
      }
      return {
        releases: purged.bucket.releases,
        payments: purged.bucket.payments,
        events: purged.bucket.events,
        platforms: purged.bucket.platforms,
        wizardProgress: purged.bucket.wizardProgress,
        lastVisibleSurface: purged.bucket.lastVisibleSurface,
        pendingDashboardDeletions: purged.bucket.pendingDashboardDeletions,
        seq: nextSeqFor(purged.bucket),
      };
    });
  },

  reset() {
    const empty = emptyPersistedBucket();
    writePersistedBucket(activeAccount.key, empty);
    set({
      releases: empty.releases,
      payments: empty.payments,
      events: empty.events,
      platforms: empty.platforms,
      wizardProgress: empty.wizardProgress,
      lastVisibleSurface: empty.lastVisibleSurface,
      pendingDashboardDeletions: empty.pendingDashboardDeletions,
      load: IDLE_LOAD,
      seq: 0,
      submitting: [],
    });
  },
}));

useAuthStore.subscribe((state) => {
  const nextAccount: PublisherAccount = {
    key: state.user?.id ?? ANONYMOUS_ACCOUNT_KEY,
    isAdmin: state.user?.isAdmin === true,
  };
  if (nextAccount.key === activeAccount.key && nextAccount.isAdmin === activeAccount.isAdmin) return;

  const bucket =
    activeAccount.key === ANONYMOUS_ACCOUNT_KEY &&
    nextAccount.key !== ANONYMOUS_ACCOUNT_KEY &&
    !hasPersistedPublisherValue(nextAccount.key)
      ? movePersistedBucket(activeAccount.key, nextAccount.key)
      : readPersistedBucket(nextAccount.key);

  activeAccount = nextAccount;
  const hydrated = developerSeededReleasedState(activeAccount, bucket);
  usePublisherStore.setState({
    releases: hydrated.releases,
    payments: hydrated.payments,
    events: hydrated.events,
    platforms: hydrated.platforms,
    wizardProgress: hydrated.wizardProgress,
    lastVisibleSurface: hydrated.lastVisibleSurface,
    pendingDashboardDeletions: hydrated.pendingDashboardDeletions,
    load: IDLE_LOAD,
    seq: nextSeqFor(hydrated),
    submitting: [],
  });
});

/** The six §6 counters, derived rather than stored. */
export interface PublisherCounts {
  total: number;
  draft: number;
  inReview: number;
  processing: number;
  live: number;
  failed: number;
}

/**
 * Counts releases by status.
 *
 * Derived on read, not maintained as fields: a stored count is a second fact that can
 * disagree with the list it counts, and the list is short.
 */
export function countReleases(releases: PublisherRelease[]): PublisherCounts {
  return {
    total: releases.length,
    draft: releases.filter((r) => r.status === 'draft').length,
    inReview: releases.filter((r) => r.status === 'in_review').length,
    processing: releases.filter((r) => r.status === 'processing').length,
    live: releases.filter((r) => r.status === 'live').length,
    failed: releases.filter((r) => r.status === 'failed').length,
  };
}

/** One release by id, or null. Mirrors selectSongById's shape. */
export const selectReleaseById = (
  releases: PublisherRelease[],
  id: string | null
): PublisherRelease | null => (id ? (releases.find((r) => r.id === id) ?? null) : null);

/** An editable draft that should be resumed, or null. */
export function selectMostRecentDraftProgress(
  releases: PublisherRelease[],
  wizardProgress: Record<string, PublisherWizardProgress>
): [string, PublisherWizardProgress] | null {
  const editableIds = new Set(releases.filter((r) => r.externalId == null).map((r) => r.id));
  const resumable = Object.entries(wizardProgress)
    .filter(([releaseId]) => editableIds.has(releaseId))
    .sort((a, b) => b[1].touchedAt.localeCompare(a[1].touchedAt));
  return resumable[0] ?? null;
}
